/**
 * Constants and utilities for country data
 * This includes ISO 3166-1 alpha-2 country codes and supported countries list
 */
import countries from 'i18n-iso-countries';
// Register languages for country names (English is the default)
import enLocale from 'i18n-iso-countries/langs/en.json';

// Initialize the library with English locale
countries.registerLocale(enLocale);

// Get all countries as an object: { countryCode: countryName }
const allCountries = countries.getNames('en');

/**
 * Lists of countries supported by different payment service providers
 */
export const SUPPORTED_SERVICES = {
  // This is where you can update which countries support which withdrawal methods
  STRIPE: [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GI', 'GR', 'HU',
    'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK',
    'SI', 'ES', 'SE', 'CH', 'GB'
  ],

  WISE: [
    //empty array means this provider is not yet supported
  ] as string[],

  AIRWALLEX: [
    //empty array means this provider is not yet supported
  ] as string[],

  REVOLUT: [
    //empty array means this provider is not yet supported
  ] as string[],

  PAYPAL: [
    //empty array means this provider is not yet supported
  ] as string[],

  NIUM: [
    //empty array means this provider is not yet supported
  ] as string[],

  THUNES: [
    //empty array means this provider is not yet supported
  ] as string[],

  ADYEN: [
    //empty array means this provider is not yet supported
  ] as string[],
};

/**
 * Get readable service names for display purposes
 */
export const SERVICE_DISPLAY_NAMES: Record<keyof typeof SUPPORTED_SERVICES, string> = {
  STRIPE: 'Stripe Connect',
  WISE: 'Wise',
  AIRWALLEX: 'Airwallex',
  REVOLUT: 'Revolut',
  PAYPAL: 'PayPal',
  NIUM: 'Nium',
  THUNES: 'Thunes',
  ADYEN: 'Adyen'
};

/**
 * Checks if a country supports a specific payment service
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @param service Payment service to check ('STRIPE', 'ALTERNATIVE_METHOD', etc.)
 * @returns Boolean indicating whether the service is available for the country
 */
export const isServiceAvailable = (countryCode: string, service: keyof typeof SUPPORTED_SERVICES): boolean => {
  if (!countryCode || !service) return false;
  return SUPPORTED_SERVICES[service].includes(countryCode);
};

/**
 * Get all available services for a specific country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Array of service keys that are available for the country
 */
export const getAvailableServices = (countryCode: string): Array<keyof typeof SUPPORTED_SERVICES> => {
  if (!countryCode) return [];

  return Object.keys(SUPPORTED_SERVICES).filter(
    service => isServiceAvailable(countryCode, service as keyof typeof SUPPORTED_SERVICES)
  ) as Array<keyof typeof SUPPORTED_SERVICES>;
};

/**
 * Get service availability information for a specific country
 * @param countryCode ISO 3166-1 alpha-2 country code
 * @returns Object with service availability information
 */
export const getServiceAvailabilityInfo = (countryCode: string): {
  availableServices: Array<keyof typeof SUPPORTED_SERVICES>;
  displayNames: string[];
  isSupported: boolean;
} => {
  const availableServices = getAvailableServices(countryCode);
  const displayNames = availableServices.map(service => SERVICE_DISPLAY_NAMES[service]);

  return {
    availableServices,
    displayNames,
    isSupported: availableServices.length > 0
  };
};

/**
 * Get all supported countries for any service as an array of objects
 * @returns Array of country objects with code and name properties
 */
export const getAllCountries = (): Array<{ code: string, name: string }> => {
  return Object.entries(allCountries).map(([code, name]) => ({
    code,
    name
  })).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Get only countries that support at least one of our payment services
 * This is useful for filtering the dropdown to only show countries we can support
 * @returns Array of country objects with code and name properties
 */
export const getSupportedCountries = (): Array<{ code: string, name: string }> => {
  // Combine all unique country codes from all services
  const allSupportedCodes = new Set<string>();

  Object.values(SUPPORTED_SERVICES).forEach(countryCodes => {
    countryCodes.forEach(code => allSupportedCodes.add(code));
  });

  // Filter all countries to only include supported ones
  return getAllCountries().filter(country => allSupportedCodes.has(country.code));
};

/**
 * Get country ISO code from country name
 * @param countryName Full country name
 * @returns ISO 3166-1 alpha-2 country code or empty string if not found
 */
export const getCountryCode = (countryName: string): string => {
  if (!countryName) return '';
  return countries.getAlpha2Code(countryName, 'en') || '';
};

/**
 * Get country name from ISO code
 * @param code ISO 3166-1 alpha-2 country code
 * @returns Full country name or the input code if not found
 */
export const getCountryNameFromCode = (code: string): string => {
  if (!code) return '';
  return countries.getName(code, 'en') || code;
}; 